import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import pushNotificationService from '../../services/pushNotificationService';
import api from '../../services/api';

/**
 * PushNotificationManager Component
 * 
 * Manages push notification setup for authenticated users
 * - Requests notification permission on mount
 * - Subscribes user to push notifications
 * - Automatically re-subscribes on login
 */
const PushNotificationManager = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    const setupPushNotifications = async () => {
      // Only setup push notifications for authenticated users
      if (!isAuthenticated || !user) {
        return;
      }

      // Check if push notifications are supported
      if (!pushNotificationService.isSupported()) {
        console.log('Push notifications not supported on this browser');
        return;
      }

      try {
        // Initialize service worker
        const initialized = await pushNotificationService.init();
        if (!initialized) {
          console.log('Failed to initialize service worker');
          return;
        }

        // Check current permission status
        const currentPermission = pushNotificationService.getPermissionStatus();
        
        if (currentPermission === 'denied') {
          console.log('Push notifications permission denied by user');
          return;
        }

        // Request permission if not granted
        if (currentPermission === 'default') {
          const granted = await pushNotificationService.requestPermission();
          if (!granted) {
            console.log('User declined push notification permission');
            return;
          }
        }

        // Get VAPID public key from backend
        const response = await api.get('/vapid-public-key');
        const vapidPublicKey = response.data.publicKey;

        // Subscribe to push notifications
        await pushNotificationService.subscribe(vapidPublicKey);
        
        console.log('Push notifications enabled successfully');
      } catch (error) {
        console.error('Error setting up push notifications:', error);
      }
    };

    setupPushNotifications();
  }, [isAuthenticated, user]);

  // This component doesn't render anything
  return null;
};

export default PushNotificationManager;
